import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Footprints, Shield, Loader2, Smartphone, Key } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Setup2FA = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showManual, setShowManual] = useState(false);
  
  useEffect(() => {
    fetchQRCode();
  }, []);
  
  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/setup`, { withCredentials: true });
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (error) {
      if (error.response?.status === 400) {
        // 2FA already configured
        navigate("/dashboard");
      } else {
        toast.error("Errore nel caricamento");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!totpCode || totpCode.length !== 6) {
      toast.error("Inserisci un codice a 6 cifre");
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, 
        { totp_code: totpCode },
        { withCredentials: true }
      );
      toast.success("2FA configurato con successo!");
      navigate("/dashboard", { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice non valido");
    } finally {
      setVerifying(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="setup-2fa-container">
        <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Configura 2FA
          </h1>
          <p className="text-slate-500 text-center mb-6">
            Scansiona il QR code con Google Authenticator
          </p>
          
          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white border-2 border-slate-200 rounded-xl">
              <img 
                src={`data:image/png;base64,${qrCode}`} 
                alt="QR Code 2FA" 
                className="w-48 h-48"
                data-testid="qr-code"
              />
            </div>
          </div>
          
          {/* Manual Entry */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowManual(!showManual)}
              className="text-sm text-blue-600 hover:underline w-full text-center"
            >
              {showManual ? "Nascondi codice manuale" : "Non riesci a scansionare? Inserisci manualmente"}
            </button>
            
            {showManual && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Codice segreto:</p>
                <p className="font-mono text-sm text-slate-900 break-all select-all" data-testid="manual-secret">
                  {secret}
                </p>
              </div>
            )}
          </div>
          
          {/* Instructions */}
          <div className="mb-6 space-y-2">
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">1</span>
              </div>
              <p>Apri Google Authenticator sul tuo telefono</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">2</span>
              </div>
              <p>Tocca "+" e poi "Scansiona codice QR"</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">3</span>
              </div>
              <p>Inserisci il codice a 6 cifre mostrato nell'app</p>
            </div>
          </div>
          
          {/* Verification Form */}
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <Label htmlFor="totp">Codice di verifica</Label>
              <div className="relative mt-1.5">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="pl-10 text-center text-xl tracking-widest font-mono"
                  data-testid="totp-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={verifying || totpCode.length !== 6}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium"
              data-testid="verify-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifica...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Attiva 2FA
                </>
              )}
            </Button>
          </form>
          
          {/* Warning */}
          <p className="text-xs text-amber-600 text-center mt-6 bg-amber-50 p-3 rounded-lg">
            ⚠️ Salva il codice segreto in un posto sicuro. Ti servirà se perdi l'accesso al telefono.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Setup2FA;
